import axios, { AxiosInstance } from 'axios';
import config from '../config';
import { User } from '../types/user';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refresh_token';

interface UserInfo {
  user?: User;
  role?: string;
  role_name?: string;
  access_token?: string;
}

class AuthService {
  private static instance: AuthService;
  private baseUrl: string;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.baseUrl = config.api.baseURL;
    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true,
    });

    // リクエストインターセプター
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          // 標準的なBearer認証を使用
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // レスポンスインターセプター
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          // 401エラーの場合、トークンが無効である可能性が高いため、認証状態をクリア
          console.log('認証サービス - 401エラーを検出。認証状態をクリア');
          this.clearAuth();
        }
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // トークン管理
  public setToken(token: string): void {
    try {
      // 長いトークン文字列を処理するため、Bearerプレフィックスを削除
      const cleanToken = token.replace('Bearer ', '');
      console.log('トークンを保存:', { tokenLength: cleanToken.length });
      
      // トークンをlocalStorageに保存
      localStorage.setItem(TOKEN_KEY, cleanToken);
      
      // 最後の認証更新時刻を記録（認証情報クリアのセーフガードのため）
      localStorage.setItem('last_auth_refresh', Date.now().toString());
      
      // グローバルなAxiosデフォルトヘッダーにも設定
      axios.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      
      // 自身のインスタンスのヘッダーにも設定
      this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${cleanToken}`;
      
      // 念のためセッションストレージにもバックアップ
      try {
        sessionStorage.setItem(TOKEN_KEY, cleanToken);
      } catch (e) {
        console.warn('セッションストレージへのトークン保存に失敗:', e);
      }
      
      // 重要: グローバルなトークン設定フラグを設定
      window['__AUTH_TOKEN_SET__'] = true;
      
      // 他のサービスに通知するためのカスタムイベントを発行
      const tokenEvent = new CustomEvent('auth:token-updated', { detail: { token: cleanToken } });
      window.dispatchEvent(tokenEvent);
      
      console.log('トークン設定完了 - グローバルヘッダー確認:', {
        hasGlobalHeader: !!axios.defaults.headers.common['Authorization'],
        hasInstanceHeader: !!this.axiosInstance.defaults.headers.common['Authorization']
      });
    } catch (error) {
      console.error('トークン保存エラー:', error);
    }
  }

  public getToken(): string | null {
    try {
      // まずlocalStorageから取得を試みる
      let token = localStorage.getItem(TOKEN_KEY);
      
      // localStorageになければsessionStorageから取得を試みる
      if (!token) {
        token = sessionStorage.getItem(TOKEN_KEY);
        if (token) {
          console.log('localStorageにトークンがないため、sessionStorageから復元');
          // 見つかった場合はlocalStorageにも保存
          localStorage.setItem(TOKEN_KEY, token);
          
          // グローバルなAxiosデフォルトヘッダーにも設定
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // 自身のインスタンスのヘッダーにも設定
          this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
      }
      
      if (!token) {
        // ログイン中やログアウト中は警告を出さない
        const isLoginInProgress = sessionStorage.getItem('login_in_progress') === 'true';
        const isLogoutInProgress = sessionStorage.getItem('logout_in_progress') === 'true';
        
        if (!isLoginInProgress && !isLogoutInProgress) {
          console.warn('トークンが見つかりません');
        }
        return null;
      }
      
      // 見つかったトークンでヘッダーを更新（念のため）
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // トークンをそのまま返す（Bearerプレフィックスなし）
      return token;
    } catch (error) {
      console.error('トークン取得エラー:', error);
      return null;
    }
  }

  public getAuthHeader(): Record<string, string> {
    const token = this.getToken();
    if (!token) return {};
    
    // Authorization ヘッダーを返す
    return {
      'Authorization': `Bearer ${token}`
    };
  }

  public setRefreshToken(token: string): void {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  public setUser(user: User): void {
    if (!user) {
      console.warn('ユーザー情報が空です');
      return;
    }

    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('ユーザー情報保存エラー:', error);
    }
  }

  public getUser(): User | null {
    try {
      const userStr = localStorage.getItem(USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('ユーザー情報取得エラー:', error);
      return null;
    }
  }

  public getUserInfo(): UserInfo {
    const user = this.getUser();
    const token = this.getToken();
    return {
      user,
      role: user?.role,
      role_name: user?.role,
      access_token: token
    };
  }

  /**
   * ログアウト処理
   * 認証情報をクリアしてログインページに遷移
   */
  public async logout(): Promise<void> {
    console.log('ログアウト処理開始');
    
    try {
      // 認証情報を即座にクリア
      this.clearAuth();
      
      // ログインページに強制遷移
      window.location.href = '/login';
    } catch (error) {
      console.error('ログアウトエラー:', error);
      // エラーが発生した場合でも強制的にログインページに遷移
      window.location.href = '/login';
    }
  }

  /**
   * 認証情報をクリアする
   */
  public clearAuth(): void {
    console.log('認証情報をクリア');
    try {
      // トークンを削除
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem('last_auth_refresh');
      
      // セッションストレージもクリア
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
      sessionStorage.clear(); // セッションストレージを完全にクリア
      
      // Axiosのデフォルトヘッダーからも削除
      delete axios.defaults.headers.common['Authorization'];
      delete this.axiosInstance.defaults.headers.common['Authorization'];
      
      // クッキーからも削除（もし存在する場合）
      document.cookie = `${TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${USER_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      
      // グローバルなトークン設定フラグをクリア
      window['__AUTH_TOKEN_SET__'] = false;
      
      console.log('認証情報のクリア完了');
    } catch (error) {
      console.error('認証情報クリア中にエラーが発生:', error);
    }
  }

  // 認証操作
  public async login(username: string, password: string): Promise<any> {
    try {
      console.log('ログインリクエストを送信:', { username, password: '***' });
      
      // ログイン処理中であることを示すフラグを設定
      sessionStorage.setItem('login_in_progress', 'true');
      
      const response = await this.axiosInstance.post('/api/auth/login', {
        username,  // emailからusernameに修正
        password
      });
      console.log('ログインレスポンス:', response.data);
      
      const { access_token, user } = response.data;
      
      if (access_token && user) {
        // ログイン成功時にリダイレクト関連のフラグをクリア
        sessionStorage.removeItem('auth_redirect_attempted');
        sessionStorage.removeItem('auth_error_count');
        sessionStorage.removeItem('redirect_count');
        sessionStorage.removeItem('login_in_progress'); // ログイン処理完了フラグをクリア
        
        // トークンを保存（この関数内でAxiosのヘッダーも設定される）
        this.setToken(access_token);
        this.setUser(user);
        
        // 確実にトークンが設定されたことを確認
        const token = this.getToken();
        console.log('ログイン後のトークン確認:', !!token);
        
        // グローバルなAxiosデフォルトヘッダーを再設定（念のため）
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          console.log('グローバルヘッダー設定完了');
        }
        
        return response.data;
      }
      throw new Error('認証情報が不完全です');
    } catch (error) {
      // エラー時にもログイン処理中フラグをクリア
      sessionStorage.removeItem('login_in_progress');
      
      console.error('ログインエラー:', error);
      if (error.response) {
        console.error('エラーレスポンス:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      throw error;
    }
  }

  /**
   * トークンを検証する
   * @returns トークンが有効かどうか
   */
  public async validateToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      console.log('トークン検証 - トークンなし');
      return false;
    }

    try {
      console.log('トークン検証を開始');

      // 長いトークンの分割送信を廃止
      /*
      if (token.length > 200) {
        const midPoint = Math.floor(token.length / 2);
        const part1 = token.substring(0, midPoint);
        const part2 = token.substring(midPoint);
        console.log('トークン検証 - 長いトークンを分割して送信:', { totalLength: token.length, part1Length: part1.length, part2Length: part2.length });
        // ここでカスタムヘッダーを設定
        this.axiosInstance.defaults.headers.common['X-Auth-Token-Part1'] = part1;
        this.axiosInstance.defaults.headers.common['X-Auth-Token-Part2'] = part2;
      }
      */

      const response = await this.axiosInstance.get(`${this.baseUrl}/api/auth/validate-token`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
        withCredentials: true,
        timeout: 10000, // 10秒のタイムアウト
      });

      if (response.data.success) {
        console.log('トークン検証成功');
        return true;
      } else {
        console.warn('トークン検証失敗:', response.data.message);
        return false;
      }
    } catch (error) {
      console.error('トークン検証エラー:', error);
      
      // エラーの詳細情報を取得
      const errorDetails = {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      };
      console.log('トークン検証エラー詳細:', errorDetails);
      
      // 検証結果と時刻を保存
      sessionStorage.setItem('last_token_validation', Date.now().toString());
      sessionStorage.setItem('last_validation_result', 'false');
      
      // リダイレクトループ検出
      const redirectAttempted = sessionStorage.getItem('auth_redirect_attempted');
      
      // 401または403エラーの場合は認証情報をクリア、ただしリダイレクトループ時はスキップ
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        if (redirectAttempted !== 'true') {
          console.log('トークン検証で401/403エラー - 認証情報をクリア');
          this.clearAuth();
          
          // リダイレクトループを防ぐためのフラグを設定
          sessionStorage.setItem('auth_redirect_attempted', 'true');
        } else {
          console.log('リダイレクトループ検出 - 認証情報のクリアをスキップ');
          // 既にリダイレクト試行中の場合は認証情報クリアをスキップ
        }
      }
      
      return false;
    }
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }
}

export default AuthService.getInstance(); 